import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUp, ChevronDown, Trash2, BarChart3, Circle, RectangleHorizontal, Check, Copy, GripVertical, Plus } from 'lucide-react';
import Layout from '../components/Layout.js';
import PhoneMock from '../components/PhoneMock.js';
import { LinkPageView } from './LinkPagePublic.js';
import { readError } from './LinkPages.js';
import { useLP } from '../linkpages/texts.js';
import { copyText } from '../utils/clipboard.js';
import {
  THEMES, BUTTON_STYLES, FONTS, ICON_OPTIONS, DEFAULT_DESIGN, resolveDesign,
  type LinkItem, type LinkPage, type LinkPageDesign,
} from '../linkpages/themes.js';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

const card = 'rounded-xl bg-panel border border-line p-5';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1';
const inputCls = 'w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30';
const btnPrimary = 'rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50';
const btnGhost = 'rounded-full border border-line px-4 py-2 text-sm font-medium text-ink-muted hover:bg-raised hover:text-ink';
const iconBtn = 'p-2 rounded-lg text-ink-muted hover:bg-raised hover:text-ink disabled:opacity-30';

const newId = () => Math.random().toString(36).slice(2, 12);

function ColorField({ id, text, value, onChange }: { id: string; text: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>{text}</label>
      <div className="flex items-center gap-2">
        <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-line bg-transparent" />
        <code className="text-xs text-ink-muted">{value}</code>
      </div>
    </div>
  );
}

/** Compact Linktree-style visibility switch for link rows (icon-only, labelled via aria). */
function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`h-[22px] w-10 shrink-0 rounded-full p-0.5 transition-colors ${checked ? 'bg-success-500' : 'bg-raised border border-line'}`}
    >
      <span className={`block h-[18px] w-[18px] rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : ''}`} />
    </button>
  );
}

export default function LinkPageEditor() {
  const lp = useLP();
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<LinkPage | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetch(`/api/link-pages/${id}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      if (cancelled) return;
      if (!res.ok) { setError(await readError(res)); return; }
      const data = (await res.json()) as LinkPage;
      setPage({ ...data, design: { ...DEFAULT_DESIGN, ...data.design } });
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const update = useCallback((patch: Partial<LinkPage>) => {
    setPage((p) => (p ? { ...p, ...patch } : p));
    setDirty(true);
    setSaved(false);
  }, []);

  const clicks = page?.clicks ?? {};
  const totalClicks = useMemo(() => Object.values(clicks).reduce((a, b) => a + b, 0), [clicks]);

  if (!page) {
    return (
      <Layout title={lp('titleSingle')}>
        {error
          ? <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-600 dark:text-danger-300">{error}</div>
          : <div className="h-40 rounded-xl bg-panel border border-line animate-pulse" />}
      </Layout>
    );
  }

  const updateDesign = (patch: Partial<LinkPageDesign>) => update({ design: { ...page.design, ...patch } });
  const updateLink = (linkId: string, patch: Partial<LinkItem>) =>
    update({ links: page.links.map((l) => (l.id === linkId ? { ...l, ...patch } : l)) });
  const addLink = (type: 'link' | 'header') =>
    update({ links: [...page.links, { id: newId(), type, label: '', url: '', icon: 'link', enabled: true }] });
  const removeLink = (linkId: string) => update({ links: page.links.filter((l) => l.id !== linkId) });
  const moveLink = (index: number, dir: -1 | 1) => {
    const links = [...page.links];
    const target = index + dir;
    if (target < 0 || target >= links.length) return;
    [links[index], links[target]] = [links[target]!, links[index]!];
    update({ links });
  };

  const selectTheme = (key: string) => {
    const preset = THEMES[key];
    if (key === 'custom') {
      // start from the colours currently shown
      const r = resolveDesign(page.design);
      updateDesign({ theme: 'custom', accent: r.accent, background: r.background, backgroundEnd: r.backgroundEnd, textColor: r.textColor });
    } else if (preset && 'accent' in preset) {
      updateDesign({ theme: key, accent: preset.accent });
    }
  };

  // Presets have a fixed palette, so changing the accent switches to "custom" and keeps the current colours.
  const changeAccent = (accent: string) => {
    if (page.design.theme === 'custom') { updateDesign({ accent }); return; }
    const r = resolveDesign(page.design);
    updateDesign({ theme: 'custom', accent, background: r.background, backgroundEnd: r.backgroundEnd, textColor: r.textColor });
  };

  async function save() {
    if (!page) return;
    setError('');
    const slug = page.slug.trim().toLowerCase();
    if (!SLUG_RE.test(slug)) { setError(lp('slugInvalid')); return; }
    // incomplete links would be dropped silently by the server, so tell the user
    const incomplete = page.links.find((l) => !l.label.trim() || (l.type === 'link' && !l.url.trim()));
    if (incomplete) { setError(lp('linkIncomplete')); return; }
    setSaving(true);
    const res = await fetch(`/api/link-pages/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...page, slug }),
    });
    setSaving(false);
    if (!res.ok) { setError(await readError(res)); return; }
    const data = (await res.json()) as LinkPage;
    setPage({ ...data, design: { ...DEFAULT_DESIGN, ...data.design } });
    setDirty(false);
    setSaved(true);
  }

  async function remove() {
    if (!confirm(lp('confirmDelete'))) return;
    const res = await fetch(`/api/link-pages/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) { setError(await readError(res)); return; }
    setDirty(false);
    navigate('/links');
  }

  async function uploadAvatar(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload/avatar', { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) { setError(await readError(res)); return; }
      const { url } = (await res.json()) as { url: string };
      update({ avatar: url });
    } finally {
      setUploading(false);
    }
  }

  async function uploadBanner(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload/banner', { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) { setError(await readError(res)); return; }
      const { url } = (await res.json()) as { url: string };
      update({ banner: url });
    } finally {
      setUploading(false);
    }
  }

  function copyPageUrl() {
    if (!page) return;
    void copyText(`${window.location.origin}/l/${page.slug}`).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Layout title={page.title || page.slug}>
      <div className="max-w-6xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-muted">{page.views ?? 0} {lp('views')} · {totalClicks} {lp('clicks')}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyPageUrl}
              className="inline-flex items-center gap-2 rounded-full bg-raised border border-line px-4 py-2 text-sm font-semibold text-ink-muted hover:text-ink"
              title={lp('copyUrl')}
            >
              /l/{page.slug}
              {copied ? <Check className="h-3.5 w-3.5 text-success-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <button type="button" className={btnGhost} onClick={() => navigate('/links')}>{lp('back')}</button>
            <a href={`/l/${page.slug}`} target="_blank" rel="noopener noreferrer" className={btnGhost}>{lp('open')}</a>
            <button type="button" onClick={() => void save()} disabled={saving} className={btnPrimary}>
              {saving ? lp('saving') : saved ? <><Check className="h-4 w-4" /> {lp('saved')}</> : lp('save')}
            </button>
          </div>
        </div>

        {error && <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-600 dark:text-danger-300">{error}</div>}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <section className={card}>
              <h2 className="mb-4 text-base font-semibold text-ink">{lp('profile')}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="lp-title-input" className={labelCls}>{lp('titleField')}</label>
                  <input id="lp-title-input" className={inputCls} value={page.title} maxLength={100} onChange={(e) => update({ title: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="lp-slug-input" className={labelCls}>{lp('address')}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ink-muted">/l/</span>
                    <input id="lp-slug-input" className={inputCls} value={page.slug} maxLength={40} onChange={(e) => update({ slug: e.target.value.toLowerCase() })} />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="lp-bio-input" className={labelCls}>{lp('bio')}</label>
                  <textarea id="lp-bio-input" className={inputCls} rows={3} maxLength={300} value={page.bio} onChange={(e) => update({ bio: e.target.value })} />
                </div>
                <div className="md:col-span-2 flex items-center gap-4">
                  {page.avatar && <img src={page.avatar} alt="" className="h-14 w-14 rounded-full object-cover" />}
                  <label className={`${btnGhost} cursor-pointer`}>
                    {uploading ? lp('uploading') : lp('uploadAvatar')}
                    <input type="file" accept="image/*" className="sr-only" data-testid="lp-avatar-input" onChange={(e) => void uploadAvatar(e.target.files?.[0])} />
                  </label>
                  {page.avatar && <button type="button" className={btnGhost} onClick={() => update({ avatar: '' })}>{lp('remove')}</button>}
                </div>
                <div className="md:col-span-2 flex items-center gap-4">
                  {page.banner && <img src={page.banner} alt="" className="h-14 w-28 rounded-lg object-cover" />}
                  <label className={`${btnGhost} cursor-pointer`}>
                    {uploading ? lp('uploading') : lp('uploadBanner')}
                    <input type="file" accept="image/*" className="sr-only" data-testid="lp-banner-input" onChange={(e) => void uploadBanner(e.target.files?.[0])} />
                  </label>
                  {page.banner && <button type="button" className={btnGhost} onClick={() => update({ banner: '' })}>{lp('remove')}</button>}
                  <span className="text-xs text-ink-faint">{lp('bannerHint')}</span>
                </div>
              </div>
            </section>

            <section className={card}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-ink">{lp('links')}</h2>
                <button type="button" className={btnGhost} onClick={() => addLink('header')}>{lp('addHeader')}</button>
              </div>
              <button
                type="button"
                onClick={() => addLink('link')}
                className="mb-4 flex w-full items-center justify-center gap-2 rounded-full bg-accent-600 py-3 text-sm font-semibold text-white transition-opacity hover:bg-accent-500"
              >
                <Plus className="h-4 w-4" /> {lp('addLink')}
              </button>
              {page.links.length === 0 && <p className="text-sm text-ink-faint">{lp('noLinks')}</p>}
              <ul className="space-y-3">
                {page.links.map((link, index) => (
                  <li key={link.id} data-testid="lp-editor-link" className="flex gap-2.5 rounded-2xl border border-line bg-panel p-4">
                    <GripVertical className="mt-1 h-4 w-4 shrink-0 cursor-grab text-ink-faint" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <input
                            aria-label={link.type === 'header' ? lp('headerText') : lp('label')}
                            className="w-full rounded-md bg-transparent px-1 py-0.5 text-[15px] font-semibold text-ink placeholder:text-ink-faint hover:bg-raised focus:bg-raised focus:outline-none"
                            placeholder={link.type === 'header' ? lp('sectionHeader') : lp('label')}
                            value={link.label}
                            maxLength={100}
                            onChange={(e) => updateLink(link.id, { label: e.target.value })}
                          />
                          {link.type === 'link' && (
                            <input
                              aria-label={lp('linkUrl')}
                              className="mt-0.5 w-full rounded-md bg-transparent px-1 py-0.5 text-sm text-ink-muted placeholder:text-ink-faint hover:bg-raised focus:bg-raised focus:outline-none"
                              placeholder="https://…"
                              value={link.url}
                              onChange={(e) => updateLink(link.id, { url: e.target.value })}
                            />
                          )}
                          {link.type === 'header' && (
                            <span className="mt-1 inline-block rounded-md bg-raised px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">{lp('header')}</span>
                          )}
                        </div>
                        <Switch
                          checked={link.enabled}
                          onChange={(v) => updateLink(link.id, { enabled: v })}
                          label={link.enabled ? lp('hideLink') : lp('showLink')}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {link.type === 'link' && (
                          <select aria-label={lp('icon')} className={`${inputCls} !w-auto !py-1 !text-xs`} value={link.icon} onChange={(e) => updateLink(link.id, { icon: e.target.value })}>
                            {ICON_OPTIONS.map((o) => <option key={o} value={o} className="bg-panel">{o}</option>)}
                          </select>
                        )}
                        {link.type === 'link' && (
                          <span className="inline-flex items-center gap-1.5" title={lp('linkColorHint')}>
                            <input
                              type="color"
                              aria-label={lp('linkColor')}
                              value={link.color || resolveDesign(page.design).accent}
                              onChange={(e) => updateLink(link.id, { color: e.target.value })}
                              className="h-7 w-9 cursor-pointer rounded border border-line bg-transparent"
                            />
                            {link.color && (
                              <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => updateLink(link.id, { color: '' })}>
                                {lp('linkColorReset')}
                              </button>
                            )}
                          </span>
                        )}
                        {link.type === 'link' && (
                          <button
                            type="button"
                            className={iconBtn}
                            aria-label={link.display === 'circle' ? lp('displayButton') : lp('displayCircle')}
                            title={link.display === 'circle' ? lp('displayButton') : lp('displayCircle')}
                            onClick={() => updateLink(link.id, { display: link.display === 'circle' ? 'button' : 'circle' })}
                          >
                            {link.display === 'circle' ? <Circle className="w-4 h-4" /> : <RectangleHorizontal className="w-4 h-4" />}
                          </button>
                        )}
                        {link.type === 'link' && (
                          <span className="inline-flex items-center gap-1 text-xs text-ink-muted"><BarChart3 className="w-3.5 h-3.5" />{clicks[link.id] ?? 0} {lp('clicks')}</span>
                        )}
                        <div className="ml-auto flex items-center">
                          <button type="button" className={iconBtn} aria-label={lp('moveUp')} disabled={index === 0} onClick={() => moveLink(index, -1)}><ChevronUp className="w-4 h-4" /></button>
                          <button type="button" className={iconBtn} aria-label={lp('moveDown')} disabled={index === page.links.length - 1} onClick={() => moveLink(index, 1)}><ChevronDown className="w-4 h-4" /></button>
                          <button type="button" className={iconBtn} aria-label={lp('deleteLink')} onClick={() => removeLink(link.id)}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className={card}>
              <h2 className="mb-4 text-base font-semibold text-ink">{lp('design')}</h2>
              <span className={labelCls}>{lp('theme')}</span>
              <div className="mb-5 flex flex-wrap gap-3">
                {Object.entries(THEMES).map(([key, t]) => {
                  const r = key === 'custom' ? resolveDesign({ ...page.design, theme: 'custom' }) : (t as { background: string; backgroundEnd: string; textColor: string });
                  const active = page.design.theme === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-label={`${lp('theme')} ${key === 'custom' ? lp('themeCustom') : t.label}`}
                      aria-pressed={active}
                      onClick={() => selectTheme(key)}
                      className={`h-14 w-20 rounded-lg border-2 text-xs font-semibold ${active ? 'border-accent-400' : 'border-transparent'}`}
                      style={{ background: `linear-gradient(160deg, ${r.background}, ${r.backgroundEnd})`, color: r.textColor }}
                    >
                      {key === 'custom' ? lp('themeCustom') : t.label}
                    </button>
                  );
                })}
              </div>

              {page.design.theme === 'custom' && (
                <div className="mb-5 flex flex-wrap gap-6">
                  <ColorField id="c-bg" text={lp('background')} value={page.design.background} onChange={(v) => updateDesign({ background: v })} />
                  <ColorField id="c-bg2" text={lp('gradientEnd')} value={page.design.backgroundEnd} onChange={(v) => updateDesign({ backgroundEnd: v })} />
                  <ColorField id="c-text" text={lp('text')} value={page.design.textColor} onChange={(v) => updateDesign({ textColor: v })} />
                </div>
              )}
              <div className="mb-5">
                <ColorField id="c-accent" text={lp('buttonColor')} value={resolveDesign(page.design).accent} onChange={changeAccent} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="d-button" className={labelCls}>{lp('buttonStyle')}</label>
                  <select id="d-button" className={inputCls} value={page.design.buttonStyle} onChange={(e) => updateDesign({ buttonStyle: e.target.value })}>
                    {Object.entries(BUTTON_STYLES).map(([k]) => <option key={k} value={k} className="bg-panel">{lp('bs_' + k)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="d-font" className={labelCls}>{lp('font')}</label>
                  <select id="d-font" className={inputCls} value={page.design.font} onChange={(e) => updateDesign({ font: e.target.value })}>
                    {Object.entries(FONTS).map(([k]) => <option key={k} value={k} className="bg-panel">{lp('font_' + k)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="d-layout" className={labelCls}>{lp('layout')}</label>
                  <select id="d-layout" className={inputCls} value={page.design.layout} onChange={(e) => updateDesign({ layout: e.target.value })}>
                    <option value="stack" className="bg-panel">{lp('layoutList')}</option>
                    <option value="grid" className="bg-panel">{lp('layoutGrid')}</option>
                    <option value="card" className="bg-panel">{lp('layoutCard')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="d-mode" className={labelCls}>{lp('mode')}</label>
                  <select id="d-mode" className={inputCls} value={page.design.mode} onChange={(e) => updateDesign({ mode: e.target.value })}>
                    <option value="auto" className="bg-panel">{lp('modeAuto')}</option>
                    <option value="light" className="bg-panel">{lp('modeLight')}</option>
                    <option value="dark" className="bg-panel">{lp('modeDark')}</option>
                  </select>
                </div>
              </div>
            </section>

            <section className={`${card} flex items-center justify-between`}>
              <div>
                <h2 className="text-base font-semibold text-ink">{lp('deletePage')}</h2>
                <p className="text-sm text-ink-muted">{lp('deletePageHint')}</p>
              </div>
              <button type="button" onClick={() => void remove()} className="rounded-full border border-danger-500/40 px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-500/10">{lp('delete')}</button>
            </section>
          </div>

          <aside className="lg:sticky lg:top-6 self-start">
            <p className={labelCls}>{lp('livePreview')}</p>
            <div data-testid="lp-preview">
              <PhoneMock>
                <LinkPageView page={page} preview />
              </PhoneMock>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
